import { batch } from "solid-js";
import { reconcile } from "solid-js/store";

import { Get, Post } from "@repo/utils/api";
import Base64 from "@repo/utils/Base64";
import FeatureStore from "@repo/utils-solid/FeatureStore";

import { api } from "..";

import { Organization } from "./organizations";
import { Staff } from "./staffs";

export type Waiver = {
  id: Waiver.Id;
  name: string;
  organizationId: Organization.Id;
  documentUrl: string;
  lastModifiedAt: Date;
  staffId: Staff.Id;
  type: Waiver.Type;
};

export namespace Waiver {
  export type Id = `wv_${string}`;
  export type Type = "general" | "membership";
  export type Params = Pick<Waiver, "name" | "type"> & {
    documentBase64: Base64;
  };
}

export type WaiversRest = {
  [id: Waiver.Id]: Get<{}, { waiver: Waiver }> & {
    signatures: Post<{}>;
  };
};

const initialState = {
  byId: {} as Record<Waiver.Id, Waiver>,
};

export const { Context, Provider, useWaivers } = FeatureStore.init(
  "Waivers",
  initialState,
  ([store, setStore], { attachAction, withLogger }) => {
    const accessors = {
      byId: attachAction(
        (id: Waiver.Id) => store.byId[id],
        (id) => actions.fetch(id),
        (id) => (res) => !id || res,
      ),
    };

    const mutations = {
      one(waiver: Waiver) {
        setStore("byId", waiver.id, reconcile(waiver));
      },
      many(waivers: Waiver[]) {
        batch(() => {
          for (const waiver of waivers) {
            mutations.one(waiver);
          }
        });
      },
    };

    const actions = withLogger("action", {
      one: mutations.one,
      many: mutations.many,
      fetch(id: Waiver.Id) {
        return api.waivers[id]!.GET({}).then(({ waiver }) => {
          mutations.one(waiver);
          return waiver;
        });
      },
      sign(id: Waiver.Id) {
        return api.waivers[id]!.signatures.POST({});
      },
    });

    return { accessors, actions };
  },
);
